import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {getSealedAuctionPrices} from '../../../utils/getSealedAuctionPrices';
import {MutedText} from '../../ListingPreview/components/MutedText';
import {PriceCard} from '../../ListingPreview/components/PriceCard';
import {buildPriceBreakdown} from '../../ListingPreview/utils/buildPriceBreakdown';

interface SealedAuctionPriceCardProps extends TestIdProps {
  auctionVehicle: AuctionVehicleDetailResponseBody['auctionVehicle'];
  vatNote?: string | Nullish;
}

/** Only the seller's published minimum and direct-purchase prices belong in a sealed bid. */
export function SealedAuctionPriceCard(props: SealedAuctionPriceCardProps) {
  const vehicle = props.auctionVehicle;
  const formatCurrency = useFormatCurrency();
  const format = (amount: number, currency: string) =>
    formatCurrency(amount, currency, 0) ?? EMPTY_PLACEHOLDER;
  const prices = getSealedAuctionPrices(vehicle).map((price) => ({
    label: i18n.t(price.labelKey),
    breakdown: buildPriceBreakdown({
      ...price,
      currency: vehicle.currency ?? DEFAULT_CURRENCY_CODE,
      originalCurrency: vehicle.originalCurrency,
      vatRate: vehicle.vatRate,
      isVatDeductible: vehicle.vatReclaimable,
    }),
  }));
  const primary = prices.at(0);
  const secondary = prices.at(1);

  return (
    <PriceCard
      label={primary?.label ?? i18n.t('general.labels.price')}
      breakdown={primary?.breakdown ?? null}
      isVatDeductible={vehicle.vatReclaimable}
      vatNote={props.vatNote}
      undisclosedMessage={primary ? undefined : i18n.t('entity.auction.labels.priceNotDisclosed')}
      data-testid={props['data-testid']}
    >
      <Show
        when={
          secondary?.breakdown ? {label: secondary.label, breakdown: secondary.breakdown} : null
        }
      >
        {(price) => (
          <Box borderTop="1px solid" borderColor="palettes.neutral.500.100" paddingTop={3}>
            <VStack spacing={1}>
              <MutedText>{price.label}</MutedText>
              <Heading
                size={3}
                color="white"
                isSingleLine
                data-testid={suffixTestId('buyNow', props)}
              >
                {format(price.breakdown.withVat.amount, price.breakdown.withVat.currency)}
              </Heading>
              <Show when={price.breakdown.withVat.original}>
                {(original) => (
                  <MutedText data-testid={suffixTestId('buyNowOriginal', props)}>
                    {`${format(original.amount, original.currency)} ${i18n.t('entity.sourcing.vehiclePreview.labels.inOriginalCurrency')}`}
                  </MutedText>
                )}
              </Show>
              <Show when={price.breakdown.withoutVat}>
                {(net) => (
                  <HStack spacing={1} wrap>
                    <MutedText data-testid={suffixTestId('buyNowWithoutVat', props)}>
                      {`${format(net.amount, net.currency)} ${i18n.t('general.labels.withoutVat')}`}
                    </MutedText>
                  </HStack>
                )}
              </Show>
            </VStack>
          </Box>
        )}
      </Show>
    </PriceCard>
  );
}
